import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
  Paper
} from '@mui/material';
import {
  Security as SecurityIcon,
  CheckCircle as CheckIcon,
  Shield as ShieldIcon,
  Lock as LockIcon,
  Verified as VerifiedIcon,
  Assessment as AssessmentIcon,
  CloudDone as CloudIcon,
  Policy as PolicyIcon
} from '@mui/icons-material';

const SecurityCompliance: React.FC = () => {
  const navigate = useNavigate();

  const securityFeatures = [
    {
      icon: <LockIcon sx={{ fontSize: 40, color: '#003366' }} />,
      title: 'Multi-Factor Authentication',
      description: 'TOTP-based MFA with backup codes for enhanced account security',
      status: 'Implemented'
    },
    {
      icon: <ShieldIcon sx={{ fontSize: 40, color: '#003366' }} />,
      title: 'Account Lockout Protection',
      description: 'Automatic lockout after failed login attempts to prevent brute force attacks',
      status: 'Implemented'
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40, color: '#003366' }} />,
      title: 'Password Security',
      description: 'Password history tracking, complexity requirements, and 90-day expiration',
      status: 'Implemented'
    },
    {
      icon: <AssessmentIcon sx={{ fontSize: 40, color: '#003366' }} />,
      title: 'Comprehensive Audit Logging',
      description: 'Complete audit trail of all security events with IP and user agent tracking',
      status: 'Implemented'
    },
    {
      icon: <CloudIcon sx={{ fontSize: 40, color: '#003366' }} />,
      title: 'Encryption at Rest & Transit',
      description: 'TLS 1.2+ for data in transit, MongoDB encryption for data at rest',
      status: 'Implemented'
    },
    {
      icon: <VerifiedIcon sx={{ fontSize: 40, color: '#003366' }} />,
      title: 'Role-Based Access Control',
      description: 'Granular permissions with admin, data-steward, and user roles',
      status: 'Implemented'
    }
  ];

  const nistControls = [
    { family: 'Access Control (AC)', implemented: 12, total: 22, percentage: 55 },
    { family: 'Audit & Accountability (AU)', implemented: 5, total: 9, percentage: 56 },
    { family: 'Awareness & Training (AT)', implemented: 2, total: 3, percentage: 67 },
    { family: 'Configuration Management (CM)', implemented: 4, total: 9, percentage: 44 },
    { family: 'Identification & Authentication (IA)', implemented: 6, total: 11, percentage: 55 },
    { family: 'System & Communications Protection (SC)', implemented: 8, total: 23, percentage: 35 }
  ];

  const deploymentBenefits = [
    'Deploy in your own secure environment (AWS GovCloud, Azure Government)',
    'Full control over data and infrastructure',
    'Open source - complete transparency and auditability',
    'No vendor lock-in - you own your deployment',
    'Include E-Unify in your CMMC assessment',
    'Comprehensive security documentation provided'
  ];

  const complianceStandards = [
    { name: 'NIST SP 800-171', status: '54% Implemented', color: '#003366' },
    { name: 'CMMC Level 2 Ready', status: 'Architecture', color: '#003366' },
    { name: 'Section 508', status: 'Compliant', color: '#2E7D32' },
    { name: 'WCAG 2.0 AA', status: 'Compliant', color: '#2E7D32' }
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <Box sx={{ 
        bgcolor: '#003366', 
        color: 'white', 
        py: 8,
        background: 'linear-gradient(135deg, #003366 0%, #004080 100%)'
      }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Chip 
                label="CMMC Level 2 Ready" 
                sx={{ 
                  bgcolor: '#B31B1B', 
                  color: 'white', 
                  fontWeight: 600,
                  mb: 2 
                }} 
              />
              <Typography 
                variant="h2" 
                component="h1"
                sx={{ 
                  fontWeight: 700, 
                  mb: 2, 
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  color: 'white'
                }}
                aria-label="Security and Compliance"
              >
                Security & Compliance
              </Typography>
              <Typography 
                variant="h5" 
                sx={{ 
                  mb: 3, 
                  fontWeight: 400,
                  color: 'white'
                }}
                aria-label="Built for Government Security Requirements"
              >
                Built for Government Security Requirements
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 4, 
                  fontSize: '1.1rem', 
                  lineHeight: 1.7,
                  color: 'white'
                }}
              >
                E-Unify is architected from the ground up to support CMMC Level 2 requirements and NIST SP 800-171 controls. 
                As an open-source platform, customers deploy E-Unify in their own secure environments and include it in their 
                CMMC assessments.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }} role="group" aria-label="Call to action buttons">
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/register')}
                  sx={{
                    bgcolor: 'white',
                    color: '#003366',
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    '&:hover': { bgcolor: '#f0f0f0' }
                  }}
                  aria-label="Get started with E-Unify registration"
                >
                  Get Started
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => window.open('/docs/security/eunify-security-controls.txt', '_blank')}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
                  }}
                  aria-label="View security documentation in new window"
                >
                  View Security Documentation
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.95)' }}>
                <Typography variant="h6" sx={{ color: '#003366', fontWeight: 600, mb: 2 }}>
                  Compliance Status
                </Typography>
                {complianceStandards.map((standard, idx) => (
                  <Box key={idx} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#003366' }}>
                        {standard.name}
                      </Typography>
                      <Chip 
                        label={standard.status} 
                        size="small" 
                        sx={{ 
                          bgcolor: standard.color, 
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.7rem'
                        }} 
                      />
                    </Box>
                    {idx < complianceStandards.length - 1 && <Divider sx={{ mt: 1 }} />}
                  </Box>
                ))}
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Security Features */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 700, color: '#003366', mb: 2 }}>
          Built-In Security Features
        </Typography>
        <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 6, maxWidth: 800, mx: 'auto' }}>
          E-Unify implements comprehensive security controls aligned with NIST SP 800-171 requirements
        </Typography>
        
        <Grid container spacing={3}>
          {securityFeatures.map((feature, idx) => (
            <Grid item xs={12} md={6} key={idx}>
              <Card sx={{ 
                height: '100%',
                borderLeft: '4px solid #003366',
                transition: 'all 0.3s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 24px rgba(0,51,102,0.15)'
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ mr: 2 }}>{feature.icon}</Box>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="h6" sx={{ color: '#003366', fontWeight: 600 }}>
                          {feature.title}
                        </Typography>
                        <Chip 
                          label={feature.status} 
                          size="small" 
                          sx={{ bgcolor: '#2E7D32', color: 'white', fontWeight: 600 }} 
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {feature.description}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* NIST SP 800-171 Controls */}
      <Box sx={{ bgcolor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 700, color: '#003366', mb: 2 }}>
            NIST SP 800-171 Control Implementation
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 6, maxWidth: 800, mx: 'auto' }}>
            E-Unify implements 54% of applicable NIST SP 800-171 Rev 2 controls, with ongoing development to reach 100%
          </Typography>
          
          <Grid container spacing={3}>
            {nistControls.map((control, idx) => (
              <Grid item xs={12} md={6} key={idx}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" sx={{ color: '#003366', fontWeight: 600, mb: 2 }}>
                    {control.family}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h4" sx={{ color: '#003366', fontWeight: 700, mr: 1 }}>
                      {control.implemented}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      of {control.total} controls implemented
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    width: '100%', 
                    height: 8, 
                    bgcolor: '#e0e0e0', 
                    borderRadius: 1,
                    overflow: 'hidden'
                  }}>
                    <Box sx={{ 
                      width: `${control.percentage}%`, 
                      height: '100%', 
                      bgcolor: '#003366',
                      transition: 'width 0.5s ease'
                    }} />
                  </Box>
                  <Typography variant="body2" sx={{ mt: 1, color: '#003366', fontWeight: 600 }}>
                    {control.percentage}% Complete
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Card sx={{ mt: 4, p: 4, bgcolor: '#e3f2fd', borderLeft: '6px solid #003366' }}>
            <Typography variant="h6" sx={{ color: '#003366', fontWeight: 600, mb: 2 }}>
              Overall Progress
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>59 of 110 total controls implemented</strong> (54% of all controls, 62% of applicable controls)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              15 controls are not applicable (N/A) for cloud-hosted applications. E-Unify is on track to implement 
              100% of applicable controls for CMMC Level 2 readiness.
            </Typography>
          </Card>
        </Container>
      </Box>

      {/* Open Source Advantage */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 700, color: '#003366', mb: 2 }}>
          The Open Source Advantage
        </Typography>
        <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 6, maxWidth: 800, mx: 'auto' }}>
          Unlike commercial platforms, E-Unify gives you complete control and transparency
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', p: 4, borderTop: '4px solid #003366' }}>
              <Typography variant="h5" sx={{ color: '#003366', fontWeight: 600, mb: 3 }}>
                How It Works
              </Typography>
              <List>
                {[
                  'Download E-Unify source code (free and open)',
                  'Deploy in your secure environment (AWS GovCloud, Azure Government)',
                  'Configure security controls per your requirements',
                  'Include E-Unify in your CMMC assessment',
                  'You get certified - not the software'
                ].map((step, idx) => (
                  <ListItem key={idx} sx={{ py: 1 }}>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#2E7D32' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={step}
                      primaryTypographyProps={{ fontWeight: 500 }}
                    />
                  </ListItem>
                ))}
              </List>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', p: 4, borderTop: '4px solid #B31B1B' }}>
              <Typography variant="h5" sx={{ color: '#003366', fontWeight: 600, mb: 3 }}>
                Your Benefits
              </Typography>
              <List>
                {deploymentBenefits.map((benefit, idx) => (
                  <ListItem key={idx} sx={{ py: 1 }}>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#2E7D32' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={benefit}
                      primaryTypographyProps={{ fontWeight: 500 }}
                    />
                  </ListItem>
                ))}
              </List>
            </Card>
          </Grid>
        </Grid>

        <Card sx={{ mt: 4, p: 4, bgcolor: '#003366', color: 'white', textAlign: 'center' }}>
          <PolicyIcon sx={{ fontSize: 60, mb: 2, opacity: 0.9 }} />
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            This is the Open Source Government Model
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, maxWidth: 800, mx: 'auto', opacity: 0.95 }}>
            Just like Linux, PostgreSQL, and Kubernetes are used in CMMC environments without being "certified" themselves, 
            E-Unify provides the secure architecture and documentation you need for your CMMC assessment.
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8, fontStyle: 'italic' }}>
            You control the deployment. You get certified. You save $500K+ vs. commercial platforms.
          </Typography>
        </Card>
      </Container>

      {/* Call to Action */}
      <Box sx={{ bgcolor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="md">
          <Card sx={{ p: 6, textAlign: 'center', borderTop: '6px solid #003366' }}>
            <Typography variant="h4" sx={{ color: '#003366', fontWeight: 700, mb: 2 }}>
              Ready to Get Started?
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.1rem' }}>
              Deploy E-Unify in your secure environment and include it in your CMMC Level 2 assessment
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/register')}
                sx={{
                  bgcolor: '#003366',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': { bgcolor: '#004080' }
                }}
              >
                Create Free Account
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/contact')}
                sx={{
                  borderColor: '#003366',
                  color: '#003366',
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': { borderColor: '#003366', bgcolor: 'rgba(0,51,102,0.04)' }
                }}
              >
                Contact Us
              </Button>
            </Box>
          </Card>
        </Container>
      </Box>
    </Box>
  );
};

export default SecurityCompliance;
